import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orderService } from '../../services/api';
import NotificationContainer from '../../components/Notification/NotificationContainer';
import styles from './MyOrders.module.css';

const MyOrders = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancellationComment, setCancellationComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  // Rediriger si non authentifié
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les commandes
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await orderService.getOrders({
        page: currentPage,
        per_page: itemsPerPage
      });

      if (response.success && response.data) {
        const ordersData = response.data.data || response.data;
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        if (response.data.current_page) {
          setPagination({
            current_page: response.data.current_page,
            last_page: response.data.last_page,
            per_page: response.data.per_page,
            total: response.data.total
          });
        }
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      addNotification('Erreur lors du chargement des commandes', 'error');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les commandes
  useEffect(() => {
    let filtered = [...orders];

    // Filtrer par statut
    if (filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Filtrer par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(searchLower) ||
        order.delivery_name?.toLowerCase().includes(searchLower) ||
        order.delivery_address?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOrders(filtered);
  }, [orders, filters]);

  // Fonction pour formater les prix
  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0';
    return numPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', color: '#f59e0b' },
      confirmed: { label: 'Confirmée', color: '#3b82f6' },
      processing: { label: 'En traitement', color: '#8b5cf6' },
      shipped: { label: 'Expédiée', color: '#06b6d4' },
      delivered: { label: 'Livrée', color: '#10b981' },
      cancelled: { label: 'Annulée', color: '#ef4444' },
      refunded: { label: 'Remboursée', color: '#6b7280' }
    };
    const statusInfo = statusMap[status] || { label: status, color: '#6b7280' };
    return (
      <span
        className={styles.statusBadge}
        style={{ backgroundColor: statusInfo.color }}
      >
        {statusInfo.label}
      </span>
    );
  };

  // Gérer le changement de filtre
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  // Voir les détails d'une commande
  const handleViewDetails = async (order) => {
    try {
      // Charger les détails complets de la commande
      const response = await orderService.getOrder(order.id);
      if (response.success && response.data?.order) {
        setSelectedOrder(response.data.order);
      } else {
        setSelectedOrder(order);
      }
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      // En cas d'erreur, utiliser les données de base
      setSelectedOrder(order);
      setShowDetailsModal(true);
    }
  };

  // Annuler une commande
  const handleCancelClick = (order) => {
    setSelectedOrder(order);
    setCancellationComment('');
    setShowCancelModal(true);
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    // Valider que le commentaire est rempli
    if (!cancellationComment || cancellationComment.trim() === '') {
      addNotification('Veuillez saisir un commentaire expliquant la raison de l\'annulation', 'error');
      return;
    }

    try {
      const response = await orderService.cancelOrder(selectedOrder.id, cancellationComment);
      if (response.success) {
        addNotification('Commande annulée avec succès', 'success');
        setShowCancelModal(false);
        setSelectedOrder(null);
        setCancellationComment('');
        await loadData();
      } else {
        addNotification(response.message || 'Erreur lors de l\'annulation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.comment?.[0] || 'Erreur lors de l\'annulation de la commande';
      addNotification(errorMessage, 'error');
    }
  };

  if (authLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={styles.container}>
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />

      <div className={styles.header}>
        <h1>Mes Commandes</h1>
        <p className={styles.subtitle}>Consultez l'historique et le statut de vos commandes</p>
      </div>

      {/* Filtres */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="status">Statut</label>
          <select
            id="status"
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className={styles.filterSelect}
          >
            <option value="all">Toutes les commandes</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmées</option>
            <option value="processing">En traitement</option>
            <option value="shipped">Expédiées</option>
            <option value="delivered">Livrées</option>
            <option value="cancelled">Annulées</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="search">Rechercher</label>
          <input
            type="text"
            id="search"
            name="search"
            placeholder="Numéro de commande, nom, adresse..."
            value={filters.search}
            onChange={handleFilterChange}
            className={styles.filterInput}
          />
        </div>
      </div>

      {/* Liste des commandes */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Chargement des commandes...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <svg className={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <h3>Aucune commande trouvée</h3>
          <p>Vous n'avez pas encore de commandes ou aucune commande ne correspond à vos critères.</p>
          <button
            className={styles.shopButton}
            onClick={() => navigate('/')}
          >
            Continuer vos achats
          </button>
        </div>
      ) : (
        <>
          <div className={styles.ordersList}>
            {filteredOrders.map((order) => (
              <div key={order.id} className={styles.orderCard}>
                <div className={styles.orderHeader}>
                  <div className={styles.orderInfo}>
                    <h3>Commande #{order.order_number || order.id}</h3>
                    <p className={styles.orderDate}>{formatDate(order.created_at)}</p>
                  </div>
                  <div className={styles.orderActions}>
                    {getStatusBadge(order.status)}
                    {order.status === 'pending' && (
                      <button
                        className={styles.cancelButton}
                        onClick={() => handleCancelClick(order)}
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.orderBody}>
                  <div className={styles.orderDetails}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Total:</span>
                      <span className={styles.detailValue}>{formatPrice(order.total_amount)} FCFA</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Articles:</span>
                      <span className={styles.detailValue}>{order.items?.length || 0} article(s)</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Livraison:</span>
                      <span className={styles.detailValue}>{order.delivery_address || 'N/A'}</span>
                    </div>
                  </div>

                  <button
                    className={styles.detailsButton}
                    onClick={() => handleViewDetails(order)}
                  >
                    Voir les détails
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Précédent
              </button>
              <span className={styles.paginationInfo}>
                Page {pagination.current_page} sur {pagination.last_page}
              </span>
              <button
                className={styles.paginationButton}
                disabled={currentPage === pagination.last_page}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedOrder && (
        <div className={styles.modalOverlay} onClick={() => setShowDetailsModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Détails de la commande #{selectedOrder.order_number || selectedOrder.id}</h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowDetailsModal(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <h3>Statut de la commande</h3>
                <div className={styles.statusInfo}>
                  {getStatusBadge(selectedOrder.status)}
                  <p className={styles.statusDate}>
                    Commandée le {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
              </div>

              <div className={styles.modalSection}>
                <h3>Articles commandés</h3>
                <div className={styles.itemsList}>
                  {selectedOrder.items?.map((item, index) => (
                    <div key={item.id || index} className={styles.itemRow}>
                      <div className={styles.itemInfo}>
                        <span className={styles.itemName}>
                          {item.product?.name || item.product_name || 'Produit supprimé'}
                        </span>
                        <span className={styles.itemQuantity}>x{item.quantity}</span>
                      </div>
                      <span className={styles.itemPrice}>
                        {formatPrice(item.unit_price || item.price || 0)} FCFA
                      </span>
                      <span className={styles.itemTotal}>
                        {formatPrice(item.total_price || (item.quantity * (item.unit_price || item.price || 0)))} FCFA
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.modalSection}>
                <h3>Informations de livraison</h3>
                <div className={styles.deliveryInfo}>
                  <p><strong>Nom:</strong> {selectedOrder.delivery_name || 'N/A'}</p>
                  <p><strong>Téléphone:</strong> {selectedOrder.delivery_phone || 'N/A'}</p>
                  <p><strong>Adresse:</strong> {selectedOrder.delivery_address || 'N/A'}</p>
                  {selectedOrder.delivery_city && (
                    <p><strong>Ville:</strong> {selectedOrder.delivery_city}</p>
                  )}
                  {selectedOrder.delivery_region && (
                    <p><strong>Région:</strong> {selectedOrder.delivery_region}</p>
                  )}
                </div>
              </div>

              {selectedOrder.delivery_person && (
                <div className={styles.modalSection}>
                  <h3>Livreur assigné</h3>
                  <div className={styles.deliveryPersonInfo}>
                    <p><strong>Nom:</strong> {selectedOrder.delivery_person.name || selectedOrder.deliveryPerson?.name}</p>
                    {selectedOrder.delivery_person.phone && (
                      <p><strong>Téléphone:</strong> {selectedOrder.delivery_person.phone}</p>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.modalSection}>
                <h3>Résumé financier</h3>
                <div className={styles.summary}>
                  <div className={styles.summaryRow}>
                    <span>Sous-total:</span>
                    <span>{formatPrice(selectedOrder.subtotal || selectedOrder.total_amount)} FCFA</span>
                  </div>
                  {selectedOrder.shipping_cost > 0 && (
                    <div className={styles.summaryRow}>
                      <span>Frais de livraison:</span>
                      <span>{formatPrice(selectedOrder.shipping_cost)} FCFA</span>
                    </div>
                  )}
                  {selectedOrder.tax_amount > 0 && (
                    <div className={styles.summaryRow}>
                      <span>Taxes:</span>
                      <span>{formatPrice(selectedOrder.tax_amount)} FCFA</span>
                    </div>
                  )}
                  <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                    <span>Total:</span>
                    <span>{formatPrice(selectedOrder.total_amount)} FCFA</span>
                  </div>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className={styles.modalSection}>
                  <h3>Notes</h3>
                  <p className={styles.notes}>{selectedOrder.notes}</p>
                </div>
              )}

              {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_comment && (
                <div className={styles.modalSection}>
                  <h3>Commentaire d'annulation</h3>
                  <p className={styles.notes} style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
                    {selectedOrder.cancellation_comment}
                  </p>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              {selectedOrder.status === 'pending' && (
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleCancelClick(selectedOrder);
                  }}
                >
                  Annuler la commande
                </button>
              )}
              <button
                className={styles.closeButton}
                onClick={() => setShowDetailsModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation d'annulation */}
      {showCancelModal && selectedOrder && (
        <div className={styles.modalOverlay} onClick={() => setShowCancelModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Annuler la commande</h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowCancelModal(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <p>Êtes-vous sûr de vouloir annuler la commande #{selectedOrder.order_number || selectedOrder.id} ?</p>
              <p className={styles.warningText}>
                Cette action est irréversible. Le stock des produits sera restauré.
              </p>

              <div className={styles.formGroup}>
                <label htmlFor="cancellationComment" className={styles.formLabel}>
                  Commentaire <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  id="cancellationComment"
                  className={styles.formTextarea}
                  placeholder="Expliquez la raison de l'annulation (obligatoire)..."
                  value={cancellationComment}
                  onChange={(e) => setCancellationComment(e.target.value)}
                  rows={4}
                  required
                />
                <p className={styles.helpText}>
                  Ce champ est obligatoire. Veuillez expliquer la raison de l'annulation.
                </p>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={handleCancelOrder}
                disabled={!cancellationComment || cancellationComment.trim() === ''}
                style={{
                  opacity: (!cancellationComment || cancellationComment.trim() === '') ? 0.5 : 1,
                  cursor: (!cancellationComment || cancellationComment.trim() === '') ? 'not-allowed' : 'pointer'
                }}
              >
                Confirmer l'annulation
              </button>
              <button
                className={styles.closeButton}
                onClick={() => setShowCancelModal(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;

